/*
 * helpers.js
 *
 * Various helper functions
 */

import moment from "moment";
import React from "react";
import Flagship from "../core/flagship";
import { Link } from "react-router";
import FileUploadFieldView from "../components/misc/FileUploadFieldView";
import UserDataModal from "../components/modals/UserDataModal";

export function throttle(fn, delay, timer = null) {
  clearTimeout(timer);
  timer = setTimeout(fn, delay);
  return timer;
}

function getLinkedTitle(data, includeKey) {
  if (
    !empty(data) &&
    !empty(data.link) &&
    !empty(data.key) &&
    !empty(data.name)
  ) {
    return (
      <Link to={data.link}>
        {includeKey === true ? "#" + data.key + " - " + data.name : data.name}
      </Link>
    );
  } else if (typeof data === "string") {
    return data;
  } else {
    return "Unknown";
  }
}

function getUnlinkedTitle(data, includeKey) {
  if (!empty(data) && !empty(data.key) && !empty(data.name)) {
    return includeKey === true ? "#" + data.key + " - " + data.name : data.name;
  } else if (typeof data === "string") {
    return data;
  } else {
    return "Unknown";
  }
}

export function getFormattedValue(
  value,
  format = "text",
  i = 0,
  mode = "view"
) {
  let formattedValue = value;

  // Format an individual value
  const formatValue = (value, format) => {
    let newValue = value;
    if (format === "date") {
      if (!empty(value)) {
        newValue = moment.unix(newValue).format("MM/DD/YYYY");
      }
    } else if (format === "time ago") {
      newValue = moment(value).fromNow();
    } else if (format === "money") {
      if (
        (typeof newValue !== "string" || newValue.charAt(0) !== "$") &&
        newValue !== ""
      ) {
        newValue = "$" + newValue;
      }
    } else if (format === "client-name") {
      newValue = getLinkedTitle(value, false);
    } else if (format === "advisr-client") {      
      newValue = value ? (
        <a target="_blank" href={`https://app.advisr.com/client/${value}`}>{value}</a>
      ) : value;
    } else if (format === "url" && mode === "view") {
      newValue = (
        <span
          dangerouslySetInnerHTML={{
            __html: '<a target="_blank" href="' + value + '">' + value + "</a>",
          }}
        ></span>
      );
    } else if (format === "filesize") {
      let i =
        value === 0 ? 0 : (i = Math.floor(Math.log(newValue) / Math.log(1024)));
      newValue =
        (value / Math.pow(1024, i)).toFixed(2) * 1 +
        " " +
        ["B", "kB", "MB", "GB", "TB"][i];
    } else if (format === "rfp-number") {
      newValue = getLinkedTitle(value, true);
    } else if (format === "unlinked-rfp") {
      newValue = getUnlinkedTitle(value, true);
    } else if (format === "io-product") {
      newValue = Flagship.config.lineItemServices.find(function (element) {
        return element.value === value;
      });
      if (!empty(newValue) && !empty(newValue.text)) {
        newValue = newValue.text;
      }
    } else if (format === "flight") {
      return (
        <div
          className="single-flight-view flight-detail-container"
          key={"flight-" + i}
        >
          <span className="flight-label">Flight #{i + 1}</span>
          <div className="flight-details">
            {" " + moment(value.startDate).format("MM/DD/YY")} -
            {" " + moment(value.endDate).format("MM/DD/YY")}
          </div>
          <div className="flight-more-details">
            {Object.keys(value).map(function (item) {
              if (
                item !== "startDate" &&
                item !== "endDate" &&
                item !== "newFlight" &&
                item !== "Total Budget" &&
                !empty(value[item])
              ) {
                return (
                  <div>
                    <strong>{item}:</strong> {" " + value[item]}
                  </div>
                );
              }
            })}
          </div>
        </div>
      );
    } else if (format === "deployment") {
      return (
        <div
          className="single-flight-view flight-detail-container"
          key={"flight-" + i}
        >
          <span className="flight-label">Deployment Date</span>
          <div className="flight-details">
            {" " + moment(value.startDate).format("MM/DD/YY")}
          </div>
          <div className="flight-more-details">
            {Object.keys(value).map(function (item) {
              if (
                item !== "startDate" &&
                item !== "endDate" &&
                item !== "newFlight" &&
                !empty(value[item])
              ) {
                return (
                  <div>
                    <strong>{item}:</strong> {" " + value[item]}
                  </div>
                );
              }
            })}
          </div>
        </div>
      );
    } else if (format === "file") {
      return <FileUploadFieldView folder={value} />;
    } else if (format === "user") {
      if (empty(value)) {
        return null;
      } else if (typeof value !== "object") {
        return <UserDataModal getUser={value} />;
      }
      return <UserDataModal {...value} />;
    }

    if (typeof newValue === "string") {
      newValue = newValue.replace("&amp;", "&");
    }

    // Convert newlines to br
    if (typeof newValue === "string" && newValue.indexOf("\n") !== -1) {
      return newValue.split("\n").map(function (item, key) {
        return (
          <span key={key}>
            {item}
            <br />
          </span>
        );
      });
    }

    return newValue;
  };
  if (Array.isArray(value)) {
    return (
      <div>
        {value.map(function (val, i) {
          return <div key={i}>{formatValue(val, format)}</div>;
        })}
      </div>
    );
  } else {
    return formatValue(formattedValue, format);
  }
}

export function addMentions(comment) {
  if (!empty(comment) && typeof comment === "string") {
    comment = comment
      .replace(
        /({[@|#]mention [a-z0-9\/\-#\)\( ]*}) ?({[@|#]mention [a-z0-9\/\-#\)\( ]*})/gim,
        "$1 / $2"
      )
      .replace(
        /({[@|#]mention [a-z0-9\/\-#\)\( ]*}) ?({[@|#]mention [a-z0-9\/\-#\)\( ]*})/gim,
        "$1 / $2"
      );

    let parts = comment.split(
      /{[@|#]mention ([a-z ]*|[#a-z0-9\-]* ?[\/a-z0-9\-]*? ?[^}]*)? ?}/gim
    );
    let subParts;

    for (let i = 1; i < parts.length; i += 2) {
      if (typeof parts[i] !== "undefined") {
        if (parts[i].startsWith("#")) {
          if (parts[i].includes("%%search%%")) {
            parts[i] = parts[i].replace(/%%[!]?search%%/gim, "");
          }

          subParts = parts[i].split(" ");
          parts[i] = (
            <a
              className="line-item-mentioned focus-line-item"
              href={subParts[1]}
              key={i}
            >
              {subParts.slice(2).join(" ")}
              <span className="key"> ({subParts[0]})</span>
            </a>
          );
        } else if (parts[i].includes("%%search%%")) {
          let subparts = parts[i].split(
            /(.*)?(%%search%%.*%%!search%%)(.*)?/gim
          );
          parts[i] = (
            <span className="user-mentioned" key={i}>
              {subparts.map((p, k) => {
                if (typeof p === "undefined") {
                  return null;
                }

                if (!p.includes("%%search%%")) {
                  return p;
                }

                return (
                  <span className="search-mentioned" key={k}>
                    {p.replace(/%%[!]?search%%/g, "")}
                  </span>
                );
              })}
            </span>
          );
        } else {
          parts[i] = (
            <span className="user-mentioned" key={i}>
              {parts[i]}
            </span>
          );
        }
      }
    }
    parts = parts.map(function (part) {
      if (typeof part === "string") {
        if (part.includes("%%search%%")) {
          let subparts = part.split(/(%%search%%.*?%%!search%%)/gim);
          part = (
            <span>
              {subparts.map((p, k) => {
                if (typeof p === "undefined") {
                  return null;
                }

                // If this part doesn't include search, still replace newlines
                if (!p.includes("%%search%%") && typeof p === "string") {
                  if (p.includes("\n")) {
                    return p
                      .replace(/%%!search%%/g, "")
                      .split("\n")
                      .map(function (item, key) {
                        return (
                          <span key={key}>
                            {item}
                            <br />
                          </span>
                        );
                      });
                  }
                  return p.replace(/%%!search%%/g, "");
                }

                return (
                  <span className="search-mentioned" key={k}>
                    {p.replace(/%%[!]?search%%/g, "")}
                  </span>
                );
              })}
            </span>
          );
        } else if (part.includes("\n")) {
          part = part.split("\n").map(function (item, key) {
            return (
              <span key={key}>
                {item}
                <br />
              </span>
            );
          });
        }
      }
      return part;
    });
    return <div>{parts}</div>;
  }
}

export function stripHTML(value) {
  return value.replace(/(<([^>]+)>)/gi, "");
}

/**
 *
 * Detect if two values are equal (works for arrays and objects)
 * @param a
 * @param b
 * @returns {boolean}
 */
export function isEqual(a, b) {
  if (typeof a !== typeof b || Array.isArray(a) !== Array.isArray(b)) {
    return false;
  } else if (Array.isArray(a)) {
    if (a === b) return true;
    if (a == null || b == null) return false;
    if (a.length !== b.length) return false;

    for (var i = 0; i < a.length; ++i) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  } else if (isObject(a)) {
    let aProps = Object.getOwnPropertyNames(a);
    let bProps = Object.getOwnPropertyNames(b);

    if (aProps.length !== bProps.length) {
      return false;
    }

    for (let i = 0; i < aProps.length; i++) {
      let propName = aProps[i];
      if (a[propName] !== b[propName]) {
        return false;
      }
    }
    return true;
  } else {
    return a === b;
  }
}

/**
 * Simple object check.
 * @param item
 * @returns {boolean}
 */
export function isObject(item) {
  return item && typeof item === "object" && !Array.isArray(item);
}

/**
 * Deep merge multiple objects.
 * @param target
 * @param ...sources
 */
export function mergeDeep(target, ...sources) {
  if (!sources.length) return target;
  const source = sources.shift();

  if (isObject(target) && isObject(source)) {
    for (const key in source) {
      if (isObject(source[key])) {
        if (!target[key]) Object.assign(target, { [key]: {} });
        mergeDeep(target[key], source[key]);
      } else {
        Object.assign(target, { [key]: source[key] });
      }
    }
  }
  return mergeDeep(target, ...sources);
}

// Asynchronous version of `setRouteLeaveHook`.
// Instead of synchronously returning a result, the hook is expected to
// return a promise.
export function setAsyncRouteLeaveHook(router, route, hook) {
  let withinHook = false;
  let finalResult = undefined;
  let finalResultSet = false;
  router.setRouteLeaveHook(route, (nextLocation) => {
    withinHook = true;
    if (!finalResultSet) {
      hook(nextLocation).then((result) => {
        finalResult = result;
        finalResultSet = true;
        if (!withinHook && nextLocation) {
          // Re-schedule the navigation
          router.push(nextLocation);
        }
      });
    }
    let result = finalResultSet ? finalResult : false;
    withinHook = false;
    finalResult = undefined;
    finalResultSet = false;
    return result;
  });
}

// Determine whether or not a field should be shown, given other field data
export function shouldShowField(field, fieldData) {
  if (
    !field.hasOwnProperty("where") ||
    field.where === "" ||
    field.where === null
  ) {
    return true;
  }

  let result = true;

  if (
    typeof field.where.map !== "function" &&
    typeof field.where === "object"
  ) {
    field.where = [field.where];
  }

  if (typeof field.where.map === "function") {
    field.where.map(function (where) {
      if (
        !where.hasOwnProperty("field") ||
        !where.hasOwnProperty("check") ||
        !where.hasOwnProperty("against")
      ) {
        result = false;
        return false;
      } else {
        switch (where.check) {
          case "=":
            if (
              empty(fieldData) ||
              !fieldData.hasOwnProperty(where.field) ||
              fieldData[where.field] !== where.against
            ) {
              result = false;
            }
            break;
          case "!=":
            if (
              !empty(fieldData) &&
              fieldData.hasOwnProperty(where.field) &&
              where.against === "" &&
              typeof fieldData[where.field] === "undefined"
            ) {
              result = false;
            } else if (fieldData[where.field] === where.against) {
              result = false;
            }
            break;
          case "in":
            if (
              empty(fieldData) ||
              !fieldData.hasOwnProperty(where.field) ||
              typeof fieldData[where.field] === "undefined" ||
              !fieldData[where.field].includes(where.against)
            ) {
              result = false;
            }
            break;
          case "not in":
            if (
              !empty(fieldData) &&
              fieldData.hasOwnProperty(where.field) &&
              typeof fieldData[where.field] !== "undefined" &&
              fieldData[where.field].includes(where.against)
            ) {
              result = false;
            }
            break;
        }
      }
    });
  }
  return result;
}

export function empty(variable) {
  return (
    typeof variable === "undefined" ||
    variable === "" ||
    variable === null ||
    (Object.keys(variable).length === 0 && variable.constructor === Object)
  );
}

export function renderFieldValue(value, format = "") {
  if (Array.isArray(value)) {
    return value.map(function (val, i) {
      return <div key={"value-" + i}>{getFormattedValue(val, format, i)}</div>;
    });
  } else {
    return getFormattedValue(value, format);
  }
}

export function clean(obj) {
  for (let propName in obj) {
    if (obj[propName] === null || obj[propName] === undefined) {
      obj[propName] = "";
    }
  }
  return obj;
}

export function getUrlParameter(name) {
  name = name.replace(/[\[]/, "\\[").replace(/[\]]/, "\\]");
  let regex = new RegExp("[\\?&]" + name + "=([^&#]*)");
  let results = regex.exec(location.search);
  return results === null
    ? ""
    : decodeURIComponent(results[1].replace(/\+/g, " "));
}

export function randomPassword() {
  let newPassword = "";
  let length = 14;
  let charset =
    "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  for (let i = 0, n = charset.length; i < length; ++i) {
    newPassword += charset.charAt(Math.floor(Math.random() * n));
  }
  return newPassword;
}

export function arraysEqual(a, b) {
  if (a instanceof Array && b instanceof Array) {
    if (a.length !== b.length)
      // assert same length
      return false;
    for (
      let i = 0;
      i < a.length;
      i++ // assert each element equal
    )
      if (!arraysEqual(a[i], b[i])) return false;
    return true;
  } else {
    return a === b; // if not both arrays, should be the same
  }
}

export function uniqueId() {
  function s4() {
    return Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
  }
  return (
    s4() +
    s4() +
    "-" +
    s4() +
    "-" +
    s4() +
    "-" +
    s4() +
    "-" +
    s4() +
    s4() +
    s4()
  );
}

export function formatTime(secs) {
  const pad = (num) => {
    return ("0" + num).slice(-2);
  };

  let minutes = Math.floor(secs / 60);
  secs = secs % 60;
  let hours = Math.floor(minutes / 60);
  minutes = minutes % 60;
  return `${pad(hours)}:${pad(minutes)}:${pad(secs)}`;
}

export function unformatTime(time) {
  let arr = time.split(":");
  if (arr[0] === "") {
    arr[0] = "0";
  }
  let dec = parseInt((arr[1] / 6) * 10, 10);
  return parseFloat(parseInt(arr[0], 10) + "." + (dec < 10 ? "0" : "") + dec);
}

export function getTimezone(market) {
  let timezone = "America/New_York";
  switch (market) {
    case "Seattle":
      timezone = "America/Los_Angeles";
      break;
    case "Phoenix":
      timezone = "America/Denver";
      break;
    case "Chicago":
      timezone = "America/Chicago";
      break;
    case "Indianapolis":
      timezone = "America/Chicago";
      break;
    case "Twin Cities":
      timezone = "America/Chicago";
      break;
    case "Northern MN":
      timezone = "America/Chicago";
      break;
    case "St. Louis":
      timezone = "America/Chicago";
      break;
  }
  return timezone;
}

export function slugify(text) {
  return text
    .toString()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^\w\-]+/g, "")
    .replace(/\-\-+/g, "-")
    .replace(/^-+/, "")
    .replace(/-+$/, "");
}

export function filename(text = "", extension = "") {
  return (
    text
      .toString()
      .replace(/\//g, "-")
      .replace(/\s+/g, " ")
      .replace(/[^\w\- ]+/g, "")
      .replace(/\-\-+/g, "-")
      .replace(/^-+/, "")
      .replace(/-+$/, "") +
    (extension !== "" ? "." + extension.replace(/^\./, "") : extension)
  );
}

export function basename(path) {
  return typeof path === "string"
    ? path.split("/").reverse()[0].split(".")[0]
    : path;
}

export function directory(path) {
  return typeof path === "string"
    ? path.substring(0, path.lastIndexOf("/") + 1)
    : path;
}

export function extension(path) {
  return typeof path === "string"
    ? path.split("/").reverse()[0].split(".").reverse()[0]
    : path;
}

export function ucfirst(text) {
  if (typeof text === "string") {
    return text.charAt(0).toUpperCase() + text.slice(1);
  }
}

export function getServiceNiceName(service) {
  let niceName = false;
  Flagship.config.lineItemServices.map((item) => {
    if (item.hasOwnProperty("value") && item.value === service)
      niceName = item.text;

    if (item.hasOwnProperty("subOptions")) {
      item.subOptions.map((subitem) => {
        if (subitem.hasOwnProperty("value") && subitem.value === service)
          niceName = item.text + " - " + subitem.text;
      });
    }
  });
  return niceName;
}




// WEBPACK FOOTER //
// ./src/utils/helpers.js